import { Col, Row, Skeleton, Table } from "antd";
import {
  ArrowLeftOutlined,
  UserOutlined,
  BankOutlined,
  PaperClipOutlined,
  EyeOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

import { downloadRouteFile } from "../../../shared/lib/fileDownload";
import { DocumentViewerModal } from "../../../features/beneficiary-document-reports/ui/DocumentViewerModal";
import { StatusPill } from "../../../shared/ui/status-pill";
import { ActionsCell, VerButton } from "../../alert-management/ui/table.styles";
import { formatTimestamp } from "../../alerted-products-management/lib/format";
import { useCentralizationTitular } from "../model/useCentralizationTitular";
import { useCentralizationDetalle } from "../model/useCentralizationDetalle";
import { useCentralizationDocuments } from "../model/useCentralizationDocuments";
import { usePdfDocumentViewer } from "../../alerted-products-documents/model/usePdfDocumentViewer";
import {
  DetailWrapper,
  DetailToolbar,
  DetailBackButton,
  SectionCard,
  SectionTitle,
  FieldGrid,
  Field,
  FieldLabel,
  FieldValue,
} from "./CentralizationDetail.styles";

const renderValue = (value) => value || "---";

const renderDate = (value) => (value ? formatTimestamp(value) : "---");

export const CentralizationDetail = ({ record, onBack, journeyId }) => {
  const { titular, loading } = useCentralizationTitular(record?.documentoTitular);
  const { detalle, loading: detalleLoading } = useCentralizationDetalle(
    record?.orderDetailId
  );
  const { documents, loading: documentsLoading } =
    useCentralizationDocuments(journeyId);
  const pdf = usePdfDocumentViewer();

  const ordenCompra = detalle?.ordenCompra;
  const gestion = detalle?.gestionLevantamiento;

  const documentColumns = [
    {
      title: "Documento",
      dataIndex: "name",
      key: "name",
      render: renderValue,
    },
    {
      title: "Tipo",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: renderValue,
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
      render: renderValue,
    },
    {
      title: "Acciones",
      key: "actions",
      width: 220,
      render: (_, item) => (
        <ActionsCell>
          {item.isPdf ? (
            <VerButton
              icon={<EyeOutlined />}
              disabled={!item.route}
              onClick={() =>
                pdf.openFromRoute(item.route, item.name || "documento.pdf")
              }
            />
          ) : null}
          <VerButton
            icon={<DownloadOutlined />}
            disabled={!item.route}
            onClick={() => downloadRouteFile(item.route, item.name || "documento")}
          >
            Descargar
          </VerButton>
        </ActionsCell>
      ),
    },
  ];

  return (
    <DetailWrapper>
      <DetailToolbar>
        <DetailBackButton icon={<ArrowLeftOutlined />} onClick={onBack}>
          Volver a Centralización
        </DetailBackButton>
      </DetailToolbar>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <SectionCard bordered={false}>
            <SectionTitle>
              <UserOutlined />
              Información del Titular
            </SectionTitle>

            {loading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
              <FieldGrid>
                <Field>
                  <FieldLabel>Nombre completo</FieldLabel>
                  <FieldValue>{renderValue(titular?.nombreCompleto)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Identificación</FieldLabel>
                  <FieldValue>{renderValue(titular?.identificacion)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>CUB</FieldLabel>
                  <FieldValue>{renderValue(titular?.cub)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Departamento</FieldLabel>
                  <FieldValue>{renderValue(titular?.departamento)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Municipio</FieldLabel>
                  <FieldValue>{renderValue(titular?.municipio)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Plan</FieldLabel>
                  <FieldValue>{renderValue(titular?.plan)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Línea productiva</FieldLabel>
                  <FieldValue>{renderValue(titular?.lineaProductiva)}</FieldValue>
                </Field>
              </FieldGrid>
            )}
          </SectionCard>
        </Col>

        <Col xs={24} lg={12}>
          <SectionCard bordered={false}>
            <SectionTitle>
              <BankOutlined />
              Información del Proveedor
            </SectionTitle>

            <FieldGrid>
              <Field>
                <FieldLabel>Nombre / Razón social</FieldLabel>
                <FieldValue>{renderValue(record?.supplier)}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>NIT</FieldLabel>
                <FieldValue>{renderValue(record?.supplierNit)}</FieldValue>
              </Field>
            </FieldGrid>
          </SectionCard>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <SectionCard bordered={false}>
            <SectionTitle>
              <FileTextOutlined />
              Información de Orden de Compra
            </SectionTitle>

            {detalleLoading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
              <FieldGrid>
                <Field>
                  <FieldLabel>No. Orden</FieldLabel>
                  <FieldValue>{renderValue(ordenCompra?.numeroOrden)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Fecha</FieldLabel>
                  <FieldValue>{renderDate(ordenCompra?.fecha)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>ID Producto</FieldLabel>
                  <FieldValue>{renderValue(ordenCompra?.idProducto)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Productos alertados</FieldLabel>
                  <FieldValue>
                    {renderValue(ordenCompra?.productosAlertados)}
                  </FieldValue>
                </Field>
              </FieldGrid>
            )}
          </SectionCard>
        </Col>

        <Col xs={24} lg={12}>
          <SectionCard bordered={false}>
            <SectionTitle>
              <CheckCircleOutlined />
              Gestión de Levantamiento
            </SectionTitle>

            {detalleLoading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
              <FieldGrid>
                <Field>
                  <FieldLabel>Tipo de gestión</FieldLabel>
                  <FieldValue>{renderValue(gestion?.tipoGestion)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Rol responsable</FieldLabel>
                  <FieldValue>{renderValue(gestion?.rolResponsable)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Estado</FieldLabel>
                  <FieldValue>
                    <StatusPill
                      backgroundColor="#DCFCE7"
                      borderColor="#86EFAC"
                      textColor="#166534"
                      minHeight="26px"
                      padding="4px 12px"
                      fontSize="12px"
                      fontWeight={800}
                      uppercase
                    >
                      {renderValue(gestion?.estado)}
                    </StatusPill>
                  </FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Fecha solicitud</FieldLabel>
                  <FieldValue>{renderDate(gestion?.fechaSolicitud)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Usuario Implementación</FieldLabel>
                  <FieldValue>
                    {renderValue(gestion?.usuarioImplementacion)}
                  </FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Fecha VoBo</FieldLabel>
                  <FieldValue>{renderDate(gestion?.fechaVobo)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Observación revisor</FieldLabel>
                  <FieldValue>
                    {renderValue(gestion?.observacionRevision)}
                  </FieldValue>
                </Field>
              </FieldGrid>
            )}
          </SectionCard>
        </Col>
      </Row>

      <SectionCard bordered={false}>
        <SectionTitle>
          <PaperClipOutlined />
          Documentos adjuntos
        </SectionTitle>

        <Table
          rowKey="uid"
          size="middle"
          columns={documentColumns}
          dataSource={documents}
          loading={documentsLoading}
          pagination={false}
          locale={{ emptyText: "La jornada no tiene documentos asociados." }}
        />
      </SectionCard>

      <DocumentViewerModal
        isOpen={pdf.viewer.isOpen}
        title="Visor de documento"
        subtitle={pdf.viewer.title}
        documentUrl={pdf.viewer.url}
        onClose={pdf.close}
        onDownload={pdf.downloadCurrent}
      />
    </DetailWrapper>
  );
};